import $ from "cash-dom";
import { grabOnInit } from "@core";
import gaSC from "@common/utils/ga";
import contactFormInit from "@common/forms/contact-form";

/**
 * /aboutus/.
 *
 * Three behaviours: the founder tabs, .in's office-gallery lightbox, and the
 * contact form the page closes with. The form is the same module /contact-us/
 * loads — one form, one implementation, so the two pages cannot drift into
 * posting different payloads.
 */

/**
 * The founder-biography tabs.
 *
 * INTERNATIONAL'S SECTION, NOT .in's — .in has no tab widget on its About page,
 * so there is nothing to port and this is written against grabon.com's own
 * markup (src/components/about-us/AboutTabsSection.tsx).
 *
 * That component holds `activeIndex` in React state and re-renders one panel.
 * A generated file has no re-render, so every panel is in the HTML and this
 * only moves `hidden` and the active classes between them. The upside is that
 * all three biographies are in the document for a reader with no JS and for
 * Google, rather than two thirds of the section being unreachable.
 *
 * The classes toggled below are the Tailwind ones the React component swaps —
 * `font-semibold text-brand` for the active tab against
 * `text-neutral hover:text-brand` for the rest. They carry no styles in this
 * repo (there is no Tailwind here; the SCSS keys off `data-state`), but they
 * are kept in step so the markup still matches what grabon.com serves.
 */
function founderTabsInit() {
  const root = $('[data-type="about-founder"]');
  if (!root.length) return;

  const tabs = root.find('[data-type="founder-tabs"] button');
  const panels = root.find('[data-type="founder-panel"]');
  if (!tabs.length || !panels.length) return;

  tabs.each(function (_, node) {
    const tab = $(node);
    tab.on("click", function () {
      if (tab.attr("data-state") === "active") return;

      const index = tab.attr("data-index");
      const panel = panels.filter(function (_, element) {
        return $(element).attr("data-id") === index;
      });
      if (!panel.length) return;

      gaSC("About Us - Founder Tabs", tab.text().trim());

      panels.attr("data-state", "inactive").attr("hidden", "hidden");
      panel.attr("data-state", "active").removeAttr("hidden");

      tabs
        .attr("data-state", "inactive")
        .removeClass("font-semibold text-brand")
        .addClass("text-neutral hover:text-brand");
      tab
        .attr("data-state", "active")
        .removeClass("text-neutral hover:text-brand")
        .addClass("font-semibold text-brand");

      // The tab strip scrolls horizontally on narrow screens; a tab reached by
      // keyboard can be off-view when it becomes active.
      if (node.scrollIntoView) {
        node.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    });
  });
}

/**
 * .in's gallery lightbox (assets-src/js/Static/about-us.js), carried across
 * behaviour for behaviour: click a tile, its `data-src` goes into #popup-image
 * and the overlay is shown; the × and a click on the backdrop close it.
 *
 * `#zoom` IS ON EVERY TILE, repeated. That is invalid HTML and it is kept,
 * because the markup, the stylesheet and this selector all agree on it —
 * `querySelectorAll` is happy to return all of them, and "fixing" the id would
 * silently leave a gallery that no longer opens.
 *
 * The section is absent on a country that publishes no gallery, so every lookup
 * here is guarded rather than assumed.
 */
function galleryInit() {
  const popup = $("#image-popup");
  if (!popup.length) return;

  const image = $("#popup-image");
  const tiles = $(".aboutGalleryImages .grid-item");

  function close() {
    popup.css("display", "none");
  }

  tiles.on("click", function () {
    const src = $(this).attr("data-src");
    if (!src) return;
    image.attr("src", src);
    popup.css("display", "block");
  });

  popup.find(".close").on("click", close);

  // A click on the backdrop itself — not on the image inside it.
  popup.on("click", function (event) {
    if (event.target === popup[0]) close();
  });

  // Not in .in, and the one addition: an overlay that traps the reader with no
  // keyboard way out is a real problem for anyone not using a mouse.
  $(document).on("keydown", function (event) {
    if (event.key === "Escape") close();
  });
}

$(function onReady() {
  grabOnInit();
  founderTabsInit();
  galleryInit();
  contactFormInit();
});
